// Member-facing copy for the IDR unlock-card lane.
//
// Lives in its own module because three callers need the same words: the QR
// handler (camera scan), the Have a code? sheet (typed), and the Next Steps mock
// (no backend at all). One card, three entry paths, one vocabulary — a member
// who scans and then types the same spent card must not get two different
// answers.
//
// Wording follows IDR's own suggested messages for each outcome, which are
// written for someone standing at a counter holding a card.

export const REDEEM_ERROR_CODES = {
  INVALID_CODE: 'INVALID_CODE',
  UNKNOWN_CODE: 'UNKNOWN_CODE',
  CODE_ALREADY_USED: 'CODE_ALREADY_USED',
  CODE_ALREADY_REDEEMED: 'CODE_ALREADY_REDEEMED',
  CODE_EXPIRED: 'CODE_EXPIRED',
  CODE_CANCELLED: 'CODE_CANCELLED',
  COLLECTION_PASS: 'COLLECTION_PASS',
  UNMAPPED_CONTENT: 'UNMAPPED_CONTENT',
  IDR_UNAVAILABLE: 'IDR_UNAVAILABLE',
  NEEDS_CG_LINK: 'NEEDS_CG_LINK',
  DEFAULT: 'DEFAULT',
};

export const REDEEM_MESSAGES = {
  COURSE_UNLOCKED: 'Course unlocked!',
  INVALID_CODE: "That code doesn't look like a voucher. Check it and try again.",
  UNKNOWN_CODE: "We don't recognise that code — check it and try again.",
  CODE_ALREADY_USED: 'This voucher has already been used.',
  // Distinct from CODE_ALREADY_USED, which refuses. This one succeeds: it is
  // THIS member re-presenting a card they already spent, so they hold the
  // course and we still open it. Only the words change — claiming a fresh
  // unlock would tell them a card burned that did not.
  CODE_ALREADY_REDEEMED: 'This code has already been redeemed.',
  CODE_EXPIRED: 'This voucher has expired. Please contact the shop.',
  CODE_CANCELLED: 'This voucher is no longer valid. Please contact the shop.',
  COLLECTION_PASS:
    "That's a book collection pass — bring it to the IDR counter to collect your book.",
  // A voucher IDR confirms but cannot map to a course is not a state worth
  // explaining to the member: there is nothing for them to re-check and no
  // action that would change the outcome. It reads as cancelled, and points at
  // the same place a cancelled voucher does.
  UNMAPPED_CONTENT: 'This voucher is no longer valid. Please contact the shop.',
  IDR_UNAVAILABLE: "We couldn't check that code just now. Please try again in a moment.",
  NEEDS_CG_LINK: 'Add your connect group first, then enter your code.',
  DEFAULT: 'Could not unlock this course. Please try again.',
};

/**
 * Copy for a known error code, falling back to the generic line rather than
 * showing a member a raw enum.
 * @param {string} code
 * @returns {string}
 */
export function redeemMessageForCode(code) {
  return REDEEM_MESSAGES[code] || REDEEM_MESSAGES.DEFAULT;
}

/**
 * Maps a failed POST /courses/redeem-book onto member-facing copy. The API owns
 * the outcome — it is the only side that talks to IDR — so we only choose words.
 *
 * Prefers the outcome in the body over the HTTP status: the status is a summary
 * (every refusal about the card is a 409) and the outcome is the detail. The
 * API raises Nest exceptions with the outcome as the exception message and has
 * no global filter, so on the wire it arrives as Nest's default
 * `{ statusCode, message: 'CODE_EXPIRED', error }` — in `message`, not `code`.
 * `code` is still read first in case a filter ever adds one.
 * @param {Error & { response?: { status?: number, data?: { code?: string, message?: string | string[] } } }} error
 * @returns {string}
 */
export function redeemErrorMessage(error) {
  const body = error?.response?.data;
  const apiCode = [body?.code, body?.message].find(
    // hasOwnProperty, not `in`: a body saying "constructor" must not resolve.
    value =>
      typeof value === 'string' && Object.prototype.hasOwnProperty.call(REDEEM_MESSAGES, value),
  );
  if (apiCode) return REDEEM_MESSAGES[apiCode];

  const status = error?.response?.status;
  if (status === 404) return REDEEM_MESSAGES.UNKNOWN_CODE;
  if (status === 409) return REDEEM_MESSAGES.CODE_ALREADY_USED;
  if (status === 503) return REDEEM_MESSAGES.IDR_UNAVAILABLE;
  return REDEEM_MESSAGES.DEFAULT;
}
